<template>
  <div id="main-content">
    <div class="content-page-banner">
      <div class="banner-content">
        <h1>{{ $route.meta.title }}</h1>
        <div class="underline"></div>
        <section v-if="!apiStatus.loading.refData && !apiStatus.fail.refData">
          <div v-html="contents.info" />
        </section>
      </div>
    </div>

    <Alert
      alertType="info"
      message="Your first premium has not been paid. You will need to pay your first premium on your policy before you can make any changes to your premium payments."
      v-if="this.membershipInfo && 'datePaidTo' in this.membershipInfo && !this.membershipInfo.datePaidTo"
    />
    <Alert v-if="!apiStatus.loading.members && !apiStatus.loading.refData && (apiStatus.fail.refData || apiStatus.fail.members)" alertType="error" />

    <ApiLoading class="loading-wrapper" v-if="apiStatus.loading.members || apiStatus.loading.refData" :apiError="false" isMemberPortal />

    <template v-else>
      <!-- Member selection -->
      <section v-if="!apiStatus.loading.refData && !apiStatus.fail.refData" class="payment-details-content">
        <div class="fr-view" v-html="contents.callOut"></div>
      </section>
      <section>
        <div class="member-detail-content">
          <div id="member-details">
            <h2>Select a member</h2>
            <p v-if="contents.selectMemberInfoText" v-html="contents.selectMemberInfoText" />
          </div>
        </div>

        <h3 class="h4 sub-heading-underlined">Members</h3>
        <ApiLoading v-if="apiStatus.loading.members || apiStatus.fail.members" :apiError="apiStatus.fail.members" isMemberPortal />
        <ol v-else class="member-wrapper">
          <li class="member" v-for="member in members" :key="`${member.firstName}-${member.lastName}`">
            <MemberTag :firstName="member.firstName" :lastName="member.lastName" :relationship="member.coveredOnMembership" />

            <router-link
              v-if="member.personCode === currentMemberDetail.personCode || (!member.keepPrivateflag && canAccess)"
              class="button secondary member-tag-btn"
              :to="{
                ...toPaymentDetails,
                query: { step: 1 },
              }"
              @click="setNavigationState({ selectedMember: member })"
            >
              View and edit
            </router-link>
            <button v-else disabled>View and edit</button>
          </li>
        </ol>
      </section>

      <!-- Related articles -->
      <section id="related-pages" v-if="!apiStatus.loading.refData && !apiStatus.fail.refData && contents.relatedArticles">
        <h2 class="hidden">Related pages</h2>
        <QuickLinks :linksData="contents.relatedArticles" />
      </section>
    </template>
  </div>
</template>

<script>
import { Alert, ApiLoading, MemberTag, QuickLinks } from '@/components';
import PaymentDetailsServices from '@/services/PaymentDetails';
import MemberDetailsServices from '@/services/MemberDetails';
import { PAYMENT_DETAILS } from '@/router/routeNames';
import { setNavigationState } from '@/router/navigationState';
import { getAccessLevel, sortMembersByRelationshipDisplay } from '@/utils';
import { AccessLevel } from '@/enums';
import PaymentDetailsMixin from './PaymentDetailsFormSteps/PaymentDetailsMixin';

export default {
  name: 'PaymentDetailsLanding',

  mixins: [PaymentDetailsMixin],

  components: {
    Alert,
    ApiLoading,
    MemberTag,
    QuickLinks,
  },

  computed: {
    toPaymentDetails() {
      return { name: PAYMENT_DETAILS };
    },

    canAccess() {
      const thisAccessLevel = getAccessLevel(this.$route.params.policyNumber, this.memberships);
      return thisAccessLevel === AccessLevel.FULL_ACCESS || thisAccessLevel === AccessLevel.POLICY_HOLDER_ACCESS;
    },
  },

  data() {
    return {
      members: [],
      contents: {},
      apiStatus: {
        loading: {
          refData: false,
          members: false,
        },
        fail: {
          refData: false,
          members: false,
        },
      },
    };
  },

  mounted() {
    this.getRefData();
    this.getMembers();
  },

  methods: {
    setNavigationState,

    async getRefData() {
      try {
        this.apiStatus.loading.refData = true;
        const data = await PaymentDetailsServices.getPaymentDetailsSelectionRefData();
        this.contents = {
          info: data?.paymentSelectionInformationText?.informationText,
          callOut: data?.paymentSelectionCalloutText?.informationText,
          relatedArticles: [data?.relatedPage1, data?.relatedPage2, data?.relatedPage3],
          selectMemberInfoText: data?.paymentSelectionSubheadingText?.informationText,
        };
      } catch (error) {
        logger.error(error);
        this.apiStatus.fail.refData = true;
      } finally {
        this.apiStatus.loading.refData = false;
      }
    },

    async getMembers() {
      try {
        this.apiStatus.loading.members = true;
        const members = await MemberDetailsServices.getMembers(this.$route.params.policyNumber);
        this.members = sortMembersByRelationshipDisplay(members);
      } catch (error) {
        logger.error(error);
        this.apiStatus.fail.members = true;
      } finally {
        this.apiStatus.loading.members = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.hidden {
  /** this class hides <h2> as per design but due to accessibility, <h2> is required, to pass accessibility, and screen reader, change opacity hence it is hidden and use as spacing between top content and quick links */
  opacity: 0.01; // use opacity instead of display none or visibility hidden to pass accessibility
  margin-bottom: 0;
  height: spacer(5); // for spacing between top content and quick links, which is 70px as per design suggested (20px is coming from <QuickLinks> style)
  overflow: hidden;
}

.member-detail-content {
  @include flex-row;
  justify-content: space-between;
  margin: 56px 0;

  p {
    max-width: 680px;
  }

  ul {
    max-width: 680px;
  }

  @include mobile {
    flex-direction: column;
    margin: 24px 0;
  }
}

.payment-details-content {
  margin-top: 56px;

  & > div ul {
    max-width: 680px;
  }
}

.member-tag-btn {
  margin-left: 24px !important;
}

section:not(.top-content) {
  &:last-child {
    margin-bottom: 0;
  }

  & > h2 {
    font-size: font-size(2);
    margin-top: 0;
  }

  .member-wrapper {
    @include grid-layout;
    @include grid-template(repeat(auto-fill, minmax(200px, 1fr)));
    row-gap: 28px;

    .member {
      display: flex;
      flex-direction: column;
      gap: 15px;
    }
  }
}

#related-pages {
  margin-bottom: 56px;

  @include mobile {
    margin-bottom: 24px;
  }
}

:deep(.call-out) {
  max-width: 300px;
}

.content-page-banner {
  margin-bottom: 16px;
}
</style>
